/**
 * Shared helpers for mapping AG Grid filterModel / sortModel to API payloads.
 */
import moment from 'moment';
import {
  formatFromDateForAPI,
  formatToDateForAPI,
  getDefaultDateRange,
} from '../../utils/datetime.utils';

export const formatAddressField = (value) => {
  if ([null, 'null', undefined, 'undefined', ''].includes(value)) {
    return '';
  }
  return value;
};

/**
 * Retail-style date column filter: FromDate + ToDate conditions.
 * Used so default/applied date ranges are visible in AG Grid filters.
 */
export const DATE_COLUMN_FILTER_PARAMS = {
  filterOptions: [
    {
      displayKey: 'fromdate',
      displayName: 'From Date',
      predicate: () => true,
      numberOfInputs: 1,
    },
    {
      displayKey: 'todate',
      displayName: 'To Date',
      predicate: () => true,
      numberOfInputs: 1,
    },
  ],
  maxNumConditions: 2,
  buttons: ['clear'],
  closeOnApply: true,
};

/**
 * Override a column def to use the shared date-range filter.
 */
export const withDateColumnFilter = (colDef = {}) => ({
  ...colDef,
  filter: 'agDateColumnFilter',
  filterParams: DATE_COLUMN_FILTER_PARAMS,
  floatingFilter: true,
});

const toApiDate = (value, endOfDay) => {
  if (value == null || value === '') return '';
  const m = moment(value);
  if (!m.isValid()) return '';
  return endOfDay ? formatToDateForAPI(m) : formatFromDateForAPI(m);
};

const toGridDate = (value) => {
  if (value == null || value === '') return null;
  const m = moment(value);
  if (!m.isValid()) return null;
  return m.format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Build AG Grid filterModel entry for a date-range column from FilterObj dates.
 */
export const buildDateRangeFilterModel = (colId, fromdate, todate) => {
  if (!colId) return {};
  const from = toGridDate(fromdate);
  const to = toGridDate(todate);
  if (!from && !to) return {};

  const conditions = [];
  if (from) {
    conditions.push({
      filterType: 'date',
      type: 'fromdate',
      dateFrom: from,
      dateTo: null,
    });
  }
  if (to) {
    conditions.push({
      filterType: 'date',
      type: 'todate',
      dateFrom: to,
      dateTo: null,
    });
  }

  if (conditions.length === 1) {
    return { [colId]: conditions[0] };
  }

  return {
    [colId]: {
      filterType: 'date',
      operator: 'AND',
      conditions,
      condition1: conditions[0],
      condition2: conditions[1],
    },
  };
};

/**
 * Seed AG Grid filter UI from FilterObj.fromdate/todate so applied defaults are visible.
 * Prefer calling AFTER the first SSRM block has loaded. Safe to call multiple times.
 */
export const seedReportDateFilter = (api, filterBody, dateColId) => {
  if (!api || !filterBody || !dateColId) return false;
  if (!filterBody.fromdate && !filterBody.todate) return false;

  const existing =
    typeof api.getFilterModel === 'function' ? api.getFilterModel() || {} : {};
  if (existing[dateColId] && filterBody.__dateFilterBound) {
    return false;
  }

  const dateModel = buildDateRangeFilterModel(
    dateColId,
    filterBody.fromdate,
    filterBody.todate
  );
  if (!Object.keys(dateModel).length) return false;

  if (typeof api.setFilterModel === 'function') {
    // Signal grid to skip column-pinning during this programmatic seed.
    api.__ilocalSeedingFilter = true;
    api.setFilterModel({ ...existing, ...dateModel });
    filterBody.__dateFilterBound = true;
    setTimeout(() => {
      api.__ilocalSeedingFilter = false;
    }, 250);
    return true;
  }
  return false;
};

/**
 * Attach SSRM datasource. Prefer: seed date filter UI first (no datasource yet → no
 * fetch), then attach datasource so the first getRows already matches the visible filter.
 * FilterObj should already carry fromdate/todate (module/constructor defaults).
 */
export const attachReportServerSideDatasource = (
  params,
  datasource,
  filterBody,
  dateColId
) => {
  if (!params || !params.api) return;

  let attached = false;
  const attach = () => {
    if (attached) return;
    attached = true;
    // Seed UI before datasource so we don't stack an extra filter refresh on load.
    if (dateColId && filterBody) {
      seedReportDateFilter(params.api, filterBody, dateColId);
    }
    if (typeof params.api.setServerSideDatasource === 'function') {
      params.api.setServerSideDatasource(datasource);
    }
  };

  const hasDates = !!(filterBody && (filterBody.fromdate || filterBody.todate));
  if (!dateColId || hasDates) {
    attach();
    return;
  }

  const tryAttach = () => {
    if (filterBody && (filterBody.fromdate || filterBody.todate)) {
      attach();
      return true;
    }
    return false;
  };
  [0, 50, 100, 200, 400].forEach((ms) => setTimeout(tryAttach, ms));
  setTimeout(attach, 600);
};

const readDateCondition = (condition) => {
  if (!condition) return { type: null, date: '' };
  const type = condition.type || null;
  const raw = condition.dateFrom || condition.dateTo || null;
  return { type, date: raw };
};

/** Restore last-7-days defaults — never allow unbounded report date queries. */
export const restoreDefaultReportDates = (body) => {
  if (!body) return body;
  const defaults = getDefaultDateRange();
  body.fromdate = formatFromDateForAPI(defaults.fromDate);
  body.todate = formatToDateForAPI(defaults.toDate);
  return body;
};

/**
 * Map AG Grid date filterModel → body.fromdate / body.todate.
 * Clearing the date filter clears API dates too (no date constraint).
 */
export const applyDateRangeFromFilterModel = (
  body,
  filterModel,
  dateColIds = []
) => {
  if (!body || !dateColIds || !dateColIds.length) return body;

  body.__dateFilterColId = dateColIds[0];

  const model = filterModel || {};
  let foundCol = null;
  for (let i = 0; i < dateColIds.length; i += 1) {
    if (model[dateColIds[i]]) {
      foundCol = dateColIds[i];
      break;
    }
  }

  if (!foundCol) {
    if (body.__dateFilterBound) {
      body.fromdate = '';
      body.todate = '';
      body.__dateFilterBound = false;
    }
    return body;
  }

  body.__dateFilterBound = true;
  const dateValue = model[foundCol];
  let fromdate = '';
  let todate = '';

  const conditions = [];
  if (dateValue.conditions && Array.isArray(dateValue.conditions)) {
    dateValue.conditions.forEach((c) => conditions.push(c));
  } else if (dateValue.condition1 || dateValue.condition2) {
    if (dateValue.condition1) conditions.push(dateValue.condition1);
    if (dateValue.condition2) conditions.push(dateValue.condition2);
  } else {
    conditions.push(dateValue);
  }

  conditions.forEach((c) => {
    const { type, date } = readDateCondition(c);
    if (type === 'fromdate') fromdate = toApiDate(date, false);
    else if (type === 'todate') todate = toApiDate(date, true);
    else if (!fromdate && date) fromdate = toApiDate(date, false);
  });

  // Empty date inputs on an active model also count as "cleared".
  if (!fromdate && !todate) {
    body.fromdate = '';
    body.todate = '';
    body.__dateFilterBound = false;
    return body;
  }

  body.fromdate = fromdate;
  body.todate = todate;
  return body;
};

/**
 * Fold AG Grid filterModel into a single searchtext string for APIs
 * that only support free-text search (uses first non-empty filter value).
 */
export const foldFiltersToSearchText = (filterModel, { excludeColIds = [] } = {}) => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return '';
  }
  const exclude = new Set(excludeColIds || []);
  const values = Object.keys(filterModel)
    .filter((colId) => !exclude.has(colId))
    .map((colId) => filterModel[colId] && filterModel[colId].filter)
    .filter((v) => v != null && String(v).trim() !== '');
  return values.length > 0 ? String(values[0]).trim() : '';
};

/**
 * Standard report applyFilterModel: date range cols → fromdate/todate,
 * remaining text filters → searchtext / searchObject.
 */
export const applyReportFilterModel = (
  body,
  filterModel,
  { dateColIds = [] } = {}
) => {
  applyDateRangeFromFilterModel(body, filterModel, dateColIds);
  body.searchtext = foldFiltersToSearchText(filterModel, { excludeColIds: dateColIds });

  // Only forward text-column filters as searchObject — never AG Grid date models.
  const textOnly = {};
  const model = filterModel || {};
  const dateSet = new Set(dateColIds || []);
  Object.keys(model).forEach((colId) => {
    if (dateSet.has(colId)) return;
    const f = model[colId];
    if (f && f.filter != null && String(f.filter).trim() !== '') {
      textOnly[colId] = f;
    }
  });
  body.searchObject = Object.keys(textOnly).length ? textOnly : null;
  return body;
};

/**
 * Maps AG Grid filterModel keys to API searchObject keys via fieldMap.
 */
export const buildSearchObject = (filterModel, fieldMap = {}) => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return '';
  }

  const searchObject = {};
  Object.keys(filterModel).forEach((colId) => {
    const filter = filterModel[colId];
    const mappedKey = fieldMap[colId] !== undefined ? fieldMap[colId] : colId;
    if (mappedKey && filter) {
      searchObject[mappedKey] = filter;
    }
  });

  return Object.keys(searchObject).length > 0 ? searchObject : '';
};

/**
 * Applies sortModel to a mutable filter/sort body using a sort map.
 */
export const applySortModel = (body, sortModel, sortMap) => {
  if (!sortModel || sortModel.length === 0 || !sortMap) {
    return body;
  }
  const first = sortModel[0];
  const mapping = sortMap[first.colId];
  if (mapping) {
    body.tableName = mapping.tableName;
    body.sortColumn = mapping.sortColumn;
    body.sortOrder = first.sort;
  }
  return body;
};

export const buildManageBoxesSearchObject = (filterModel) =>
  buildSearchObject(filterModel, {
    name: 'name',
    description: 'description',
    'type.name': 'type',
    type: 'type',
    'address.city': 'city',
    city: 'city',
    'address.state': 'state',
    state: 'state',
    'address.zip': 'zip',
    zip: 'zip',
    'account.name': 'username',
    parentAccount: 'username',
  });

export const buildManageAccountsSearchObject = (filterModel) =>
  buildSearchObject(filterModel, {
    name: 'name',
    'type.name': 'type',
    type: 'type',
    'parent.name': 'parent',
    parent_account: 'parent',
    'contact.address.city': 'city',
    city: 'city',
    'contact.address.state': 'state',
    state: 'state',
    'contact.address.zip': 'zip',
    zip: 'zip',
  });



// WEBPACK FOOTER //
// ./src/components/grid/filterUtils.js